import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import Server from '../../../../Server';
import { FAVOURITE_STATION, FAVOURITE_STATION_SUMMARY } from '../../../../API/Endpoint';

const FALLBACK_ERROR = 'Something went wrong. Please try again.';

// `page` is 0-based on every favourites endpoint, so `page=0` must reach the
// server. Only unset values and blank strings are dropped — never a number,
// so no amount of falsiness can silently remove page 0.
const isBlank = (value) =>
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');

const buildQuery = (params) => {
    const query = new URLSearchParams();
    Object.keys(params).forEach((key) => {
        if (isBlank(params[key])) return;
        query.append(key, params[key]);
    });
    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
};

export const getFavouriteSummary = createAsyncThunk(
    'favouriteStations/getSummary',
    async (_, thunkAPI) => {
        try {
            const response = await Server.get(FAVOURITE_STATION_SUMMARY, true);
            if (response?.success) return response?.payload;
            return thunkAPI.rejectWithValue(response?.message || FALLBACK_ERROR);
        } catch (error) {
            return thunkAPI.rejectWithValue(FALLBACK_ERROR);
        }
    }
);

export const getFavouriteStations = createAsyncThunk(
    'favouriteStations/getStations',
    async ({ station_name, connector_type, page = 0, limit } = {}, thunkAPI) => {
        try {
            const response = await Server.get(
                FAVOURITE_STATION +
                    buildQuery({ station_name, connector_type, page, limit }),
                true
            );
            if (response?.success) return response?.payload;
            toast.error(response?.message || FALLBACK_ERROR);
            return thunkAPI.rejectWithValue(response?.message || FALLBACK_ERROR);
        } catch (error) {
            return thunkAPI.rejectWithValue(FALLBACK_ERROR);
        }
    }
);

export const addFavouriteStation = createAsyncThunk(
    'favouriteStations/add',
    async ({ charging_station_id }, thunkAPI) => {
        try {
            const { status, body } = await Server.postWithStatus(
                FAVOURITE_STATION,
                { charging_station_id },
                true
            );
            // 201 newly added, 200 already a favourite — the endpoint is
            // idempotent, so both mean "this station is saved now".
            if (status === 201 || status === 200 || body?.success) {
                return { charging_station_id };
            }
            return thunkAPI.rejectWithValue(body?.message || FALLBACK_ERROR);
        } catch (error) {
            return thunkAPI.rejectWithValue(FALLBACK_ERROR);
        }
    }
);

export const removeFavouriteStation = createAsyncThunk(
    'favouriteStations/remove',
    async ({ charging_station_id }, thunkAPI) => {
        try {
            const { status, body } = await Server.deleteWithStatus(
                `${FAVOURITE_STATION}/${charging_station_id}`,
                true
            );
            // 404 means it was already removed — the end state the user asked
            // for, so the UI treats it as a success.
            if (status === 200 || status === 404 || body?.success) {
                return { charging_station_id };
            }
            return thunkAPI.rejectWithValue(body?.message || FALLBACK_ERROR);
        } catch (error) {
            return thunkAPI.rejectWithValue(FALLBACK_ERROR);
        }
    }
);

const initialState = {
    // Dashboard card
    summary: null,
    summaryLoading: false,
    summaryError: null,
    // Favourites list
    stations: [],
    count: 0, // total favourites matching the filters, not the page size
    page: 0, // next page to request (0-based)
    hasMore: true,
    loading: false,
    error: null,
    favouriteOverrides: {},
};

const favouriteStationSlice = createSlice({
    name: 'favouriteStations',
    initialState,
    reducers: {
        setFavouriteOverride(state, action) {
            const { id, is_favourite } = action.payload;
            if (!id) return;
            state.favouriteOverrides[id] = is_favourite;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFavouriteSummary.pending, (state) => {
                state.summaryLoading = true;
                state.summaryError = null;
            })
            .addCase(getFavouriteSummary.fulfilled, (state, action) => {
                state.summaryLoading = false;
                state.summary = action.payload || null;
                state.summaryError = null;
            })
            .addCase(getFavouriteSummary.rejected, (state, action) => {
                state.summaryLoading = false;
                // Keep whatever we last showed; the card drops its subtitle
                // rather than breaking the dashboard.
                state.summaryError = action.payload || action.error?.message;
            })

            .addCase(getFavouriteStations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getFavouriteStations.fulfilled, (state, action) => {
                const requestedPage = action.meta?.arg?.page || 0;
                const rows = action.payload?.favourite_stations || [];
                state.loading = false;
                state.error = null;
                // Order is most-recently-favourited first — keep it as sent.
                state.stations =
                    requestedPage === 0 ? rows : [...state.stations, ...rows];
                // `count` is the TOTAL here (unlike the discovery endpoint,
                // where it is the length of the page), so pagination can
                // compare it against how much we hold.
                state.count = action.payload?.count ?? state.stations.length;
                state.page = requestedPage + 1;
                state.hasMore = rows.length > 0 && state.stations.length < state.count;
            })
            .addCase(getFavouriteStations.rejected, (state, action) => {
                state.loading = false;
                state.hasMore = false;
                state.error = action.payload || action.error?.message;
            });
    },
});

export const { setFavouriteOverride } = favouriteStationSlice.actions;

// The user's own taps win over whatever the last response happened to carry.
export const resolveIsFavourite = (overrides, station) => {
    const override = overrides?.[station?.id];
    return override === undefined ? Boolean(station?.is_favourite) : override;
};
export default favouriteStationSlice.reducer;
